import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EipStorage } from '../../../eip-shared/src/lib/eip.storage';
import { ENVIRONMENT_CONFIG, FrontendEnvironment } from '@schir-int-client/environment';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { LOCATION_TOKEN } from '../../../tech/src/lib/location.token';
import {
	loadUserAction,
	loadUserSuccessAction,
	logInUserAction,
	logInUserSuccessAction,
	logOutUserAction,
	logOutUserSuccessAction,
	unauthorizeUserAction,
} from './user.actions';
import { UserService } from './user.service';

@Injectable()
export class UserEffects {
	private redirected: boolean = false;

	constructor(
		private actions: Actions,
		private userService: UserService,
		private eipStorage: EipStorage,
		@Inject(ENVIRONMENT_CONFIG) private envConfig: FrontendEnvironment,
		@Inject(LOCATION_TOKEN) private location: Location
	) {}

	loadUser = createEffect(() =>
		this.actions.pipe(
			ofType(logInUserAction),
			switchMap(() => {
				return this.userService.getUser().pipe(map(user => logInUserSuccessAction({ user })));
			})
		)
	);

	logout = createEffect(() =>
		this.actions.pipe(
			ofType(logOutUserAction),
			filter(() => this.envConfig.authenticationType !== 'SAMOA'),
			switchMap(() => {
				this.eipStorage.clear();
				return this.userService.logout().pipe(
					tap(() => {
						this.redirect(this.envConfig.loginUrl);
					}),
					map(() => logOutUserSuccessAction())
				);
			})
		)
	);

	logoutSamoa = createEffect(
		() =>
			this.actions.pipe(
				ofType(logOutUserAction),
				filter(() => this.envConfig.authenticationType === 'SAMOA'),
				tap(() => {
					this.eipStorage.clear();
					this.redirect(this.envConfig.logoutUrl);
				})
			),
		{ dispatch: false }
	);

	unauthorized = createEffect(
		() =>
			this.actions.pipe(
				ofType(unauthorizeUserAction),
				tap(() => {
					this.redirect(this.envConfig.loginUrl);
				})
			),
		{ dispatch: false }
	);

	loadUserByResource = createEffect(() =>
		this.actions.pipe(
			ofType(loadUserAction),
			switchMap(({ resource, linkRel }) => {
				return this.userService
					.loadUserByResource(resource, linkRel)
					.pipe(map(user => loadUserSuccessAction({ user })));
			})
		)
	);

	redirect(url: string) {
		if (!this.redirected) {
			this.location.href = url;
			this.redirected = true;
		}
	}
}
